import { LineupStatus } from '@uefadigital/panenka';
import { PkButton, PkDivider, PkIcon } from '@uefadigital/panenka-react';
import i18n from 'i18next';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useGetMatchDetailsSectionsQuery } from 'api/cmsApi';
import { FormGuide } from 'components/FormGuide';
import { HeadToHead } from 'components/HeadToHead';
import { LineUpsOverview } from 'components/LineUpsOverview';
import { MatchHighlights } from 'components/MatchHighlights';
import { MatchOfficials } from 'components/MatchOfficials';
import { Regulations } from 'components/Regulations';
import { TopPlayerStats } from 'components/TopPlayerStats';
import useIsDesktop from 'hooks/useIsDesktop';
import { useMatchData } from 'hooks/useMatchData';
import { routes } from 'routes';
import { isFemale } from 'utils/helpers';

export const MatchOverviewTab = () => {
  const { competition, matchData, seasonYear, matchId } = useMatchData();

  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const { data: visibilityOptions, isLoading: visibilityLoading } = useGetMatchDetailsSectionsQuery({
    matchId,
    competitions: competition.id,
    language: i18n.language,
  });

  const getLoadingComponent = () => {
    return <div style={{ height: '100%' }} />;
  };

  if (visibilityLoading) {
    return getLoadingComponent();
  }

  const female = isFemale(competition?.id);

  const HeadToHeadComponent = <HeadToHead matchData={matchData} competitionId={competition.id} isDesktop={isDesktop} />;
  const RegulationsComponent = <Regulations isDesktop={isDesktop} />;
  const FormGuideComponent = <FormGuide isDesktop={isDesktop} matchData={matchData} />;
  const MatchOfficialsComponent = (
    <MatchOfficials refereeData={matchData.referees} isDesktop={isDesktop} isFemale={female} />
  );
  const MatchHighlightsComponent = (
    <MatchHighlights isDesktop={isDesktop} competitionId={competition.id} matchId={matchData.id} />
  );
  const fieldSize = {
    desktop: 100,
    mobile: 99,
    desktopJerseySize: 30,
    mobileJerseySize: 28,
  };

  const LineUpsComponent = (
    <LineUpsOverview match={matchData} isDesktop={isDesktop} fieldSize={fieldSize} competition={competition} />
  );
  const matchOfficialsRedirect = () => {
    const link = `${location.pathname}/${routes.officials}`.replace(/\/{2,}/g, '/');

    navigate(link);
  };

  return (
    <div className="pk-container">
      <div className="pk-col pk-col--span-12 pk-col--span-md-7">
        <div className="pk-bg--elevation-01">{!visibilityOptions?.disableHeadToHead && HeadToHeadComponent}</div>
        <div className="pk-bg--elevation-01 pk-mt--l">{!visibilityOptions?.disableFormGuide && FormGuideComponent}</div>
        {isDesktop && <div className="pk-bg--elevation-01 pk-mt--l">{MatchHighlightsComponent}</div>}
        {!isDesktop && (
          <>
            <div className="pk-bg--elevation-01">
              {!visibilityOptions?.disableLineUps &&
                matchData.lineupStatus !== LineupStatus.NOT_AVAILABLE &&
                LineUpsComponent}
            </div>
            <div className="pk-mt--l">{MatchHighlightsComponent}</div>
          </>
        )}
      </div>
      {!isDesktop && (
        <div className="pk-col pk-col--span-12">
          <TopPlayerStats competitionId={competition.id} seasonYear={seasonYear} isFemale={female} />
        </div>
      )}
      <div className="pk-col pk-col--span-12 pk-col--span-md-5">
        <div className="pk-bg--elevation-01">
          {isDesktop && (
            <div className="pk-bg--elevation-01">
              {!visibilityOptions?.disableLineUps &&
                matchData.lineupStatus !== LineupStatus.NOT_AVAILABLE &&
                LineUpsComponent}
            </div>
          )}
          {!visibilityOptions?.disableLineUps && matchData.lineupStatus !== LineupStatus.NOT_AVAILABLE && isDesktop && (
            <PkDivider class="divider--horizontal" direction="horizontal" />
          )}
          {!!matchData?.referees?.length && (
            <Fragment>
              {!visibilityOptions?.disableMatchOfficials && (
                <div className="pk-px--s pk-py--m">
                  {MatchOfficialsComponent}
                  <div className="pk-my--s pk-mx--l pk-text--right">
                    <PkButton appearance="text" onClick={matchOfficialsRedirect}>
                      <span>{t('see_match_officials_details')}</span>
                      <PkIcon name="navigation-chevron-right" />
                    </PkButton>
                  </div>
                </div>
              )}
              {!visibilityOptions?.disableMatchOfficials && (
                <PkDivider class="divider--horizontal" direction="horizontal" />
              )}
            </Fragment>
          )}
          <div>{RegulationsComponent}</div>
        </div>
      </div>
      {isDesktop && (
        <div className="pk-col pk-col--span-12">
          <TopPlayerStats competitionId={competition.id} seasonYear={seasonYear} isFemale={female} />
        </div>
      )}
    </div>
  );
};
